import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SelectAllOutlinedIcon from "@mui/icons-material/SelectAllOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";
const fmtAUD = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const AUTO_SELECT_MAX = 20;

export default function BatchesTab() {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    setResult(null);
    phappsRequest(API_PATHS.ZEPHYR_PROCESSING_MATCHED_BATCHES, "GET")
      .then(res => setBatches(res?.data ?? []))
      .catch(() => setError("Failed to load matched batches."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const autoSelect20 = () => {
    const ids = [];
    let runningInvoices = 0;
    for (const b of batches) {
      const claimCount = Number(b.ClaimCount || 0);
      if (runningInvoices + claimCount > AUTO_SELECT_MAX) break;
      ids.push(b.ScrBatchID);
      runningInvoices += claimCount;
    }
    setSelected(new Set(ids));
  };

  const selectedBatches = batches.filter(b => selected.has(b.ScrBatchID));
  const totalInvoices = selectedBatches.reduce((s, b) => s + Number(b.ClaimCount || 0), 0);
  const totalAmt = selectedBatches.reduce((s, b) => s + Number(b.TotalInvAmount || 0), 0);

  const handleGenerate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    setError(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_GENERATE_INVOICES, "POST", {
        scrBatchIDs: [...selected],
      });
      setResult(res?.data ?? {});
      setSelected(new Set());
      setBatches(prev => prev.filter(b => !selected.has(b.ScrBatchID)));
    } catch {
      setError("Invoice generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography fontWeight={700} fontSize="1rem" color={Z_PURPLE}>Batches Ready to Accept</Typography>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {batches.length} batch{batches.length !== 1 ? "es" : ""} ready · {selected.size} selected
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectAllOutlinedIcon />}
            disabled={batches.length === 0}
            onClick={autoSelect20}
            sx={{ borderColor: Z_PURPLE, color: Z_PURPLE, fontWeight: 600 }}
          >
            Auto-select 20
          </Button>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ReceiptOutlinedIcon />}
            disabled={selected.size === 0 || generating}
            onClick={() => setConfirmOpen(true)}
            sx={{ background: Z_PURPLE, "&:hover": { background: "#2a1240" }, fontWeight: 700, borderRadius: 2 }}
          >
            Generate Invoices
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography fontWeight={700}>Invoices generated</Typography>
          <Typography variant="body2">{result.totalBatches} batch{result.totalBatches !== 1 ? "es" : ""} processed · {result.totalClaims} invoice{result.totalClaims !== 1 ? "s" : ""} created</Typography>
          <Button size="small" sx={{ mt: 0.5, color: Z_BLUE }} onClick={load}>Refresh</Button>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress sx={{ color: Z_PURPLE }} />
        </Box>
      ) : batches.length === 0 && !result ? (
        <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
          <Typography>No batches ready to accept. Process scripts first.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "none" }}>
          <Table size="small">
            <TableHead sx={{ background: `${Z_PURPLE}08` }}>
              <TableRow>
                <TableCell padding="checkbox" />
                {["Batch Code", "Chemist", "Batch Date", "Scripts", "Invoices", "Inv Total"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map(b => (
                <TableRow
                  key={b.ScrBatchID}
                  hover
                  selected={selected.has(b.ScrBatchID)}
                  onClick={() => toggleRow(b.ScrBatchID)}
                  sx={{
                    cursor: "pointer",
                    background: selected.has(b.ScrBatchID) ? `${Z_BLUE}10` : undefined,
                    "&:hover": { background: selected.has(b.ScrBatchID) ? `${Z_BLUE}18` : "#f8fafc" },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(b.ScrBatchID)}
                      size="small"
                      sx={{ "&.Mui-checked": { color: Z_PURPLE } }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleRow(b.ScrBatchID)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{b.ScrBatchCode}</TableCell>
                  <TableCell>{b.ChemistName || "—"}</TableCell>
                  <TableCell>{fmtDate(b.ScrBatchDate)}</TableCell>
                  <TableCell>{b.ScriptCount}</TableCell>
                  <TableCell>{b.ClaimCount}</TableCell>
                  <TableCell>{fmtAUD(b.TotalInvAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff", py: 1.5, fontWeight: 700 }}>
          Generate Invoices
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography mb={1.5}>You are about to generate invoices for:</Typography>
          <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Batches</Typography>
              <Typography fontWeight={700}>{selectedBatches.length}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Invoices to create</Typography>
              <Typography fontWeight={700}>{totalInvoices}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Total value</Typography>
              <Typography fontWeight={700}>{fmtAUD(totalAmt)}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            sx={{ background: Z_PURPLE, "&:hover": { background: "#2a1240" }, fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
